import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '../common/Dialog';
import ModalActions from '../common/ModalActions';
import Notice from '../common/Notice';
import { Spinner } from '../common/AsyncState';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../Toast';
import { PRO_ACCESS, resolveProAccess } from '../../utils/proAccessGuard';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';
import '../../styles/design-system/index.css';
// Firestore 이벤트 트래킹
import { trackProModalOpen, trackProModalClose, trackFreeTrialClick, trackFreeTrialStart } from '../../utils/eventTracker';
// GA4 이벤트 트래킹
import { trackShowUpgradeModal, trackFreeLimitReached } from '../../utils/analytics';
import { getApiBaseUrl } from '../../utils/apiConfig';
import {
  fetchTrialUsed, isTrialAlreadyUsedError, loadCachedTrialUsed, saveCachedTrialUsed,
} from '../../utils/trialStatus';
import {
  buildStartTrialMetaPayload,
  createStartTrialEventId,
  trackMetaStartTrialPixel,
} from '../../utils/paymentTracking';
import './ProUpgradeModal.css';

// PRO 혜택 아이콘 (토스 스타일 아이콘 컨테이너)
const ProIcon = () => (
  <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="56" height="56" rx="16" fill="#E8F3FF"/>
    <path d="M28 14L32.09 22.26L41 23.27L34.5 29.64L36.18 38.52L28 34.27L19.82 38.52L21.5 29.64L15 23.27L23.91 22.26L28 14Z" fill="#3182F6"/>
    <circle cx="28" cy="28" r="6" fill="white"/>
    <path d="M26 28L27.5 29.5L30.5 26.5" stroke="#3182F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M13.333 4L6 11.333 2.667 8" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// feature별 맞춤 문구 및 혜택 (교사에게 훅! 하는 멘트)
const getFeatureContent = (feature) => {
  const contents = {
    '학급 전체 검사': {
      title: '제출 직전 밤샘 검토, 이제 끝',
      subtitle: '30명 기재요령 검사, 1분이면 완료',
      benefits: [
        '기재요령 위반 한 번에 발견',
        '수정본까지 자동 생성',
        '나이스에 바로 붙여넣기'
      ]
    },
    '학급 전체 생성': {
      title: '연말 생기부 야근, 이제 안녕',
      subtitle: '30명 생기부 5분이면 끝나요',
      benefits: [
        '학생 명단만 업로드하면 끝',
        '개인별 맞춤 문장 자동 작성',
        '학급 결과 엑셀 다운로드'
      ]
    },
    '비슷한 문장': {
      title: '한 문장으로 학급 분량까지',
      subtitle: '30개부터 프로 · 한 번에 150개',
      benefits: [
        '30·50·100·150개 한 번에 생성',
        '고급 추론으로 사실은 지키고 표현은 다양하게',
        '엑셀로 내려받아 바로 배분'
      ]
    },
    '파일 첨부': {
      title: '엑셀, PDF도 AI가 분석해요',
      subtitle: '수업 자료 정리가 순식간에',
      benefits: [
        '성적표 분석해서 맞춤 피드백',
        '교안 PDF 요약본 자동 생성',
        '학생 자료 기반 생기부 작성'
      ]
    },
    '폴더 담기': {
      title: '폴더째 놓으면 자료함에 한 번에',
      subtitle: '폴더 안 문서를 모아 두고 AI가 찾아 써요',
      benefits: [
        '폴더 안 HWP·PDF·워드 문서를 한 번에 담기',
        '채팅에서 필요할 때 자료함에서 찾아 쓰기',
        '학생 정보로 보이는 파일은 빼고 담기'
      ]
    },
    '고급 AI 모델': {
      title: '더 똑똑한 AI, 더 정확한 결과',
      subtitle: '복잡한 질문도 막힘없이',
      benefits: [
        '최신 AI 모델 무제한 사용',
        '전문가 수준의 답변 퀄리티',
        '교육 맞춤 최적화된 응답'
      ]
    },
    '상위 모델': {
      title: '가장 강력한 AI를 쓰세요',
      subtitle: '긴 문서도 정확하게 분석',
      benefits: [
        '최상위 AI 모델 풀액세스',
        '긴 문서도 한 번에 처리',
        '전문가 수준의 결과물'
      ]
    },
    '실험실': {
      title: '채점 지옥에서 탈출하세요',
      subtitle: 'AI가 서술형 채점 + 피드백까지',
      benefits: [
        '100명 채점 10분이면 끝',
        '학생별 맞춤 피드백 자동',
        '이미지 생성 기능 추가'
      ]
    },
    '팩트체크': {
      title: '제출 후 수정 요청, 사전에 막기',
      subtitle: '2026 기재요령 100% 반영',
      benefits: [
        '위반 항목 즉시 발견',
        '수정 제안까지 자동 생성',
        '맞춤법·금지어 동시 검사'
      ]
    },
    '채점': {
      title: '주말까지 채점하지 마세요',
      subtitle: '100명 서술형, 10분이면 끝',
      benefits: [
        'AI가 기준대로 자동 채점',
        '학생별 상세 피드백 생성',
        '채점 결과 엑셀로 저장'
      ]
    },
    '평가계획서': {
      title: '연초 평가계획서, 10분이면 끝',
      subtitle: '작년 계획서 올리면 AI가 올해 버전으로',
      benefits: [
        '작년 양식 그대로 유지',
        '배점·시기·방법 자유 수정',
        'HWP, PDF 파일도 분석 가능'
      ]
    },
    // 계획서 종류별 문구 — /assessment-plan 의 planConfig.label 과 매칭 (PRO가 실제로 여는 것: 파일 업로드·상세 모드)
    '지도안': {
      title: '공개수업 지도안, 작년 것으로 10분',
      subtitle: '작년 지도안 파일 올리면 올해에 맞게',
      benefits: [
        '작년 지도안 양식 그대로 유지',
        '최신 교육과정으로 성취기준 확인',
        'HWP, PDF 파일도 분석 가능'
      ]
    },
    '프로젝트 수업': {
      title: '프로젝트 수업 설계, 성취기준만 있으면',
      subtitle: 'GRASPS 기반 프로젝트를 AI가 짜드려요',
      benefits: [
        '최신 교육과정으로 성취기준 확인',
        '기존 계획서 파일 올려 이어 만들기',
        'GRASPS 요소 + 차시별 계획 자동 설계'
      ]
    },
    '서논술형': {
      title: '서논술형 문항·채점기준, 10분이면 끝',
      subtitle: '성취기준 입력하면 문항과 기준표까지',
      benefits: [
        '최신 교육과정으로 성취기준 확인',
        '기존 평가 자료 파일도 분석 가능',
        '문항·채점기준·예시답안까지 한 번에'
      ]
    },
    '탐구질문': {
      title: '탐구질문, 성취기준에서 바로',
      subtitle: '단원별 핵심·보조 탐구질문을 AI가',
      benefits: [
        '최신 교육과정으로 성취기준 확인',
        '단원 자료 파일 올려 더 정확하게',
        '핵심 질문 + 차시별 하위 질문 설계'
      ]
    },
    '자율시간': {
      title: '자율시간 33차시 계획, 한 번에',
      subtitle: '성취기준과 주제 입력하면 차시별 계획까지',
      benefits: [
        '최신 교육과정으로 성취기준 확인',
        '기존 계획서 파일 올려 이어 만들기',
        'HWP, PDF 파일도 분석 가능'
      ]
    },
    'PPT 만들기': {
      title: '수업 PPT, AI가 만들어드려요',
      subtitle: '디자인 PPT를 40장까지',
      benefits: [
        '디자인을 입힌 PPT 만들기',
        '발표자 노트까지 자동 생성',
        '교과서 PDF 기반 PPT 자동 생성'
      ]
    },
    '학습지': {
      title: '학습지, 이제 직접 만들지 마세요',
      subtitle: '이미지 학습지까지 자동 생성',
      benefits: [
        '이미지 학습지 다운로드 (PRO)',
        'OX·단답형·서술형 자동 출제',
        '정답지까지 함께 생성'
      ]
    },
    '이미지 생성': {
      title: '수업 이미지, AI가 만들어요',
      subtitle: '색칠공부부터 포스터까지',
      benefits: [
        '고품질 AI 이미지 생성',
        '참조 이미지 업로드 지원',
        '7가지 용도 × 5가지 스타일'
      ]
    }
  };

  // feature에서 키워드 매칭
  const keywords = Object.keys(contents);
  for (const key of keywords) {
    if (feature.includes(key) || key.includes(feature)) {
      return contents[key];
    }
  }

  // 기본값 (교사에게 훅!)
  return {
    title: '선생님, 야근 그만하세요',
    subtitle: '생기부·채점·수업 준비, AI가 대신해요',
    benefits: [
      '30명 생기부 5분 완성',
      '서술형 채점 자동화',
      `${TEACHER_COUNT_LABEL} 선생님이 사용 중`
    ]
  };
};

// API 호출 함수
const startFreeTrial = async (idToken, triggerFeature, triggerPage, eventId) => {
  const response = await fetch(`${getApiBaseUrl()}/api/payment/subscription/free-trial`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify(buildStartTrialMetaPayload({
      eventId,
      triggerFeature,
      triggerPage,
    }))
  });
  return response.json();
};

/**
 * 통합 PRO 업그레이드 모달 (Dialog 프리미티브 위)
 * - 혜택 먼저, 채워진 버튼은 하나만 (체험 가능하면 체험, 아니면 결제)
 * - 데스크톱 중앙 다이얼로그 / 모바일 바텀시트, ESC·스크림·X로 닫힌다
 * - props API 유지: { isOpen, onClose, feature, onTrialStart, trigger }
 */
const ProUpgradeModal = ({
  isOpen,
  onClose,
  feature = "이 기능", // 어떤 기능을 위한 업그레이드인지
  onTrialStart = null, // 체험 시작 후 콜백 (선택적)
  trigger = 'limit_reached',
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { subscriptionStatus, fetchSubscriptionStatus } = useSubscription();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [trialUsed, setTrialUsed] = useState(null); // null = 확인 중, true/false = 확인 완료
  const [error, setError] = useState(null);
  const [accessVerified, setAccessVerified] = useState(false);
  const onCloseRef = useRef(onClose);
  const showToastRef = useRef(showToast);
  const subscriptionStatusRef = useRef(subscriptionStatus);

  onCloseRef.current = onClose;
  showToastRef.current = showToast;
  subscriptionStatusRef.current = subscriptionStatus;

  // feature별 맞춤 콘텐츠
  const content = getFeatureContent(feature);

  // 마지막으로 확인한 값을 먼저 보여 주고 서버 값으로 갱신한다. 서버가 늦어도 체험 버튼이 로딩에 묶이지 않는다.
  const checkTrialStatus = useCallback(async (uid) => {
    const cached = loadCachedTrialUsed(uid);
    if (cached !== null) setTrialUsed(cached);
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setTrialUsed(true); // 로그인 안 됨 → 체험 버튼 숨김
        return;
      }

      const idToken = await user.getIdToken();
      setTrialUsed(await fetchTrialUsed(idToken, { uid }));
    } catch (err) {
      console.error('체험 상태 확인 실패:', err);
      if (cached === null) setTrialUsed(true); // 확인 못 했고 캐시도 없으면 체험 버튼 숨김
    }
  }, []);

  // 실제 서버 구독을 확인한 뒤에만 업그레이드 내용을 노출하고 이벤트를 기록한다.
  useEffect(() => {
    let cancelled = false;

    if (!isOpen) {
      setAccessVerified(false);
      return undefined;
    }

    setAccessVerified(false);
    setTrialUsed(null);
    setError(null);

    const trackConfirmedOpen = () => {
      trackProModalOpen(feature, trigger);
      if (trigger === 'limit_reached') {
        trackFreeLimitReached(feature);
      }
      trackShowUpgradeModal(trigger, feature);
    };

    const verifyAccess = async () => {
      // 비로그인 사용자는 구독 오탐 대상이 아니므로 기존 업그레이드 안내를 유지한다.
      if (!currentUser) {
        setAccessVerified(true);
        checkTrialStatus();
        trackConfirmedOpen();
        return;
      }

      const access = await resolveProAccess({
        subscriptionStatus: subscriptionStatusRef.current,
        fetchSubscriptionStatus,
        forceRefresh: true,
      });
      if (cancelled) return;

      if (access === PRO_ACCESS.PRO) {
        onCloseRef.current();
        showToastRef.current('이미 PRO를 이용 중이에요', 'info');
        return;
      }

      if (access === PRO_ACCESS.UNKNOWN) {
        onCloseRef.current();
        showToastRef.current(
          'PRO 이용 여부를 확인하지 못했어요. 잠시 후 다시 시도해주세요.',
          'warning'
        );
        return;
      }

      setAccessVerified(true);
      checkTrialStatus(currentUser.uid);

      // 서버가 FREE임을 확인한 모달만 분석 이벤트로 기록한다.
      trackConfirmedOpen();
    };

    verifyAccess();
    return () => {
      cancelled = true;
    };
  }, [isOpen, feature, trigger, currentUser, fetchSubscriptionStatus, checkTrialStatus]);

  // 7일 무료 체험 시작
  const handleStartTrial = async () => {
    // 무료 체험 클릭 이벤트 추적
    trackFreeTrialClick(feature);

    setIsLoading(true);
    setError(null);

    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setError('로그인이 필요해요');
        setIsLoading(false);
        return;
      }

      const idToken = await user.getIdToken();
      const currentPage = window.location.pathname;
      const eventId = createStartTrialEventId();
      const result = await startFreeTrial(idToken, feature, currentPage, eventId);

      if (result.success) {
        // 무료 체험 시작 성공 이벤트 추적
        trackFreeTrialStart(feature);
        saveCachedTrialUsed(currentUser?.uid, true);
        trackMetaStartTrialPixel({
          contentName: feature,
          eventId: result.meta_event_id || eventId,
        });

        // 성공 알림 표시
        showToast('7일 무료 체험을 시작했어요. PRO 기능을 바로 써보세요', 'success', 5000);

        onClose();

        // 성공 시 콜백 또는 페이지 새로고침 (구독 상태 갱신)
        if (onTrialStart) {
          onTrialStart();
        } else {
          // 새로고침하여 구독 상태 갱신 (현재 페이지 유지)
          window.location.reload();
        }
      } else {
        if (isTrialAlreadyUsedError(result.error)) {
          saveCachedTrialUsed(currentUser?.uid, true);
          setTrialUsed(true);
        }
        setError(result.error || '체험을 시작하지 못했어요. 잠시 후 다시 시도해주세요');
      }
    } catch (err) {
      setError('네트워크 연결을 확인해주세요');
    } finally {
      setIsLoading(false);
    }
  };

  // 모달 닫기 (트래킹 포함) — X·스크림·ESC는 모두 'dismiss'로 들어온다
  const handleClose = (action = 'close') => {
    trackProModalClose(feature, action);
    onClose();
  };

  // PRO 결제 페이지로 이동
  const handleProUpgrade = () => {
    handleClose('go_to_pricing');
    navigate('/pricing');
  };

  if (!isOpen) return null;

  // 서버 구독 확인 중: 닫을 수 없는 작은 다이얼로그 + 스피너 (내용은 아직 보여주지 않는다)
  if (!accessVerified) {
    return (
      <Dialog
        align="center"
        aria-busy
        dismissible={false}
        icon={<Spinner size={32} thickness={2} label="PRO 이용 여부를 확인하는 중" />}
        open
        panelClassName="pro-modal pro-modal--access-check"
        size="sm"
        testId="pro-modal-access-check"
        title="PRO 이용 여부를 확인하고 있어요"
      />
    );
  }

  // 체험 미사용(또는 확인 중)이면 체험이 유일한 채워진 버튼, 결제는 회색 보조 버튼
  const trialAvailable = trialUsed !== true;
  const checkingTrial = trialUsed === null;
  const actions = trialAvailable
    ? {
      primary: {
        label: '7일 무료 체험 시작하기',
        onClick: handleStartTrial,
        loading: isLoading || checkingTrial,
        testId: 'pro-modal-trial',
      },
      secondary: { label: '바로 결제하기', onClick: handleProUpgrade, testId: 'pro-modal-upgrade' },
    }
    : {
      primary: { label: 'PRO 시작하기', onClick: handleProUpgrade, testId: 'pro-modal-upgrade' },
    };

  return (
    <Dialog
      align="center"
      description={content.subtitle}
      footer={(
        <>
          <ModalActions {...actions} testId="pro-modal-actions" />
          {trialAvailable && (
            <p className="pro-modal__footnote">
              카드 등록 없이 바로 시작해요 · 체험 후 첫 결제 50% 할인
            </p>
          )}
        </>
      )}
      icon={<ProIcon />}
      onClose={() => handleClose('dismiss')}
      open
      panelClassName="pro-modal"
      size="sm"
      testId="pro-modal"
      title={content.title}
    >
      <ul className="pro-modal__benefits">
        {content.benefits.map((benefit) => (
          <li className="pro-modal__benefit" key={benefit}>
            <CheckIcon />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      {error && (
        <Notice compact testId="pro-modal-error" variant="error">
          {error}
        </Notice>
      )}
    </Dialog>
  );
};

export default ProUpgradeModal;
